/**
 * Thin logging wrapper around `console`. Components and services must log
 * through this module rather than calling `console` directly — the `no-console`
 * lint rule is enforced everywhere except here (see eslint.config.js), so this
 * file is the single sanctioned console boundary.
 *
 * This is also where logs reach Sentry: `warn` leaves a breadcrumb and `error`
 * sends an event, so a handled failure a service already logs (a failed upload,
 * a rejected request) is reported without every call site knowing about Sentry.
 * All `Sentry.*` calls are no-ops until `VITE_SENTRY_DSN` is set.
 */
import * as Sentry from '@sentry/react'

/** A JSON-safe value acceptable in a structured log context. */
type LogValue = string | number | boolean | null

/** Structured context attached to a log line (keys → primitive values). */
export interface LogContext {
  [key: string]: LogValue | undefined
}

function timestamp(): string {
  return new Date().toISOString()
}

const logger = {
  info(message: string, context?: LogContext): void {
    console.info(`[INFO] ${timestamp()}: ${message}`, context ?? '')
  },
  warn(message: string, context?: LogContext): void {
    console.warn(`[WARN] ${timestamp()}: ${message}`, context ?? '')
    // A breadcrumb, not an event — warnings are context for whatever fails next.
    // `data` is spread in only when present: exactOptionalPropertyTypes rejects
    // an explicit `undefined`.
    Sentry.addBreadcrumb({ level: 'warning', message, ...(context ? { data: context } : {}) })
  },
  error(message: string, context?: LogContext): void {
    console.error(`[ERROR] ${timestamp()}: ${message}`, context ?? '')
    // An event carrying the message, not captureException: this signature takes a
    // string, and callers pass an already-stringified cause (see
    // `toErrorMessage`). Genuine exceptions with stack traces still arrive via the
    // ErrorBoundary and the SDK's global handlers.
    //
    // Nor captureMessage, which the SDK now gives a stack trace. Sentry then
    // titles the issue after the top frame, so every one read "logger.error"
    // instead of what failed, and the stack only ever showed this function.
    Sentry.captureEvent({ message, level: 'error', extra: { ...context } })
  },
}

/**
 * Normalize a caught value (typed `unknown` in a `catch`) to a human-readable
 * message without writing an `unknown` annotation. The generic parameter is
 * inferred from the argument, so a `catch (error)` value flows in cleanly and
 * `app/no-unknown-type` is satisfied.
 */
export function toErrorMessage<E>(error: E): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'An unexpected error occurred'
}

export default logger
